/**
 * Which backend a build logs through, parsed from an untrusted string.
 *
 * An app reads the raw value (`import.meta.env.VITE_LOG_BACKEND`) and must
 * never let a malformed one start POSTing to a network sink. In retells the
 * one caller is `apps/retells/src/logging/pageBackend.ts`, which builds the
 * page's single backend for both the normal logging composition and the
 * early error capture that runs before it — one parse per page, so the two
 * cannot disagree about a malformed value.
 *
 * Pure and environment-agnostic like the rest of this package (see
 * core-fields.ts): it reads no env of its own, it just classifies whatever
 * string it is handed.
 */

/** The backends a build can be configured to log through. */
export type LogBackendChoice = "console" | "axiom" | "both";

const CHOICES: readonly LogBackendChoice[] = ["console", "axiom", "both"];

/**
 * Map a configured value onto a backend choice, falling back to `"console"`.
 *
 * `"console"` is the fallback rather than `"both"` deliberately: an
 * unrecognized value is a misconfiguration, and a misconfiguration must not
 * be the thing that starts POSTing events to a network sink. Console-only
 * degrades to the zero-infrastructure default a fresh clone already runs
 * with, which is also why the unset case (`undefined`) lands here.
 *
 * The caller is the right place to *notice* the fallback — a config value
 * that was ignored deserves a one-time `console.warn` naming it — but not
 * the right place to decide what it means.
 */
export function parseLogBackend(value: unknown): LogBackendChoice {
  return CHOICES.find((choice) => choice === value) ?? "console";
}
